import type { DataQualityReport } from './sweetSpotEvaluationEngine';
import type { SweetSpotEvaluationResult, SweetSpotPointResult } from './types';

type Props = {
  mode: 'preparation' | 'result';
  results: SweetSpotPointResult[];
  enabledParameterCount: number;
  quality: DataQualityReport;
  structuredResult: SweetSpotEvaluationResult | null;
  reservoirTypeLabel?: string;
  dataConfidence?: number;
  confidenceLevel?: 'high' | 'medium' | 'low';
  confidenceReasons?: string[];
  engineeringRisk?: number;
  riskLevel?: 'low' | 'medium' | 'high';
  riskItems?: string[];
};

const formatPercent = (value: number | null) => value == null ? '—' : `${value.toFixed(1)}%`;

const confidenceLabel = (level: 'high' | 'medium' | 'low' | undefined) => {
  if (level === 'high') return '高';
  if (level === 'medium') return '中';
  if (level === 'low') return '低';
  return '—';
};

const riskLabel = (level: 'low' | 'medium' | 'high' | undefined) => {
  if (level === 'high') return '高';
  if (level === 'medium') return '中';
  if (level === 'low') return '低';
  return '—';
};

export default function SweetSpotSummaryCards({
  mode, results, enabledParameterCount, quality, structuredResult,
  reservoirTypeLabel: reservoirLabel, dataConfidence, confidenceLevel,
  confidenceReasons = [], engineeringRisk, riskLevel: riskLevelValue, riskItems = [],
}: Props) {
  const valid = results.filter((result) => result.ssi != null && result.grade != null);
  const averageSSI = valid.length
    ? valid.reduce((sum, result) => sum + result.ssi!, 0) / valid.length
    : null;
  const percent = (grades: string[]) => valid.length
    ? valid.filter((result) => result.grade && grades.includes(result.grade)).length / valid.length * 100
    : null;

  const ssiValues = valid.map((item) => item.ssi!).filter(Number.isFinite);
  const metrics = mode === 'result' ? [
    { label: '评价点数', value: `${results.length} 个` },
    { label: 'Ⅰ—Ⅱ级井段占比', value: formatPercent(percent(['I', 'II'])) },
    { label: '推荐井段数量', value: `${structuredResult?.highQualityIntervals.length ?? 0} 段` },
    { label: '推荐分段数量', value: `${structuredResult?.recommendedStageBoundaries.length ?? 0} 段` },
    { label: 'SSI 平均值', value: averageSSI == null ? '—' : averageSSI.toFixed(3) },
    {
      label: 'SSI 变化范围',
      value: ssiValues.length
        ? Math.min(...ssiValues).toFixed(3) + '–' + Math.max(...ssiValues).toFixed(3)
        : '—',
    },
  ] : [
    { label: '数据完整度', value: formatPercent(quality.completeness * 100) },
    { label: '可用指标数', value: `${quality.availableIndicatorIds.length} 项` },
    { label: '缺失指标数', value: `${quality.missingIndicatorIds.length} 项` },
    { label: '预计评价点数', value: `${quality.pointCount} 个` },
    { label: '启用指标数', value: `${enabledParameterCount} 项` },
  ];
  if (mode === 'result' && reservoirLabel) metrics.push({ label: '储层类型', value: reservoirLabel });
  if (mode === 'result' && dataConfidence != null) {
    metrics.push({
      label: '数据置信度',
      value: `${confidenceLabel(confidenceLevel)}（${dataConfidence.toFixed(2)}）`,
    });
  }
  if (mode === 'result' && engineeringRisk != null) {
    metrics.push({
      label: '工程风险',
      value: `${riskLabel(riskLevelValue)}（${engineeringRisk.toFixed(2)}）`,
    });
  }

  const confidenceDetail = mode === 'result' && dataConfidence != null && confidenceReasons.length > 0
    ? confidenceReasons.join('；')
    : null;
  const riskDetail = mode !== 'result' || engineeringRisk == null
    ? null
    : riskItems.length > 0
      ? riskItems.join('；')
      : '暂无风险参数数据，工程风险按低处理（辅助判断）。';

  return (
    <div className="sweetspot-summary-grid" aria-label="评价统计摘要">
      {metrics.map((metric) => (
        <div key={metric.label} className="sweetspot-summary-card" title={
          metric.label === '数据置信度' ? (confidenceDetail ?? undefined)
            : metric.label === '工程风险' ? (riskDetail ?? undefined) : undefined
        }>
          <div className="sweetspot-summary-label">{metric.label}</div>
          <div className="sweetspot-summary-value">{metric.value}</div>
        </div>
      ))}
    </div>
  );
}
